import React, { useEffect } from "react";
import clsx from "clsx";
import { Trans } from "react-i18next";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { useCompanyDashboardAccess } from "apps/company/api/rest/company-dashboard-access";
import { DashboardEngagementProvider } from "apps/company/api/rest/company-dashboard-engagement";
import { DashboardResilienceProvider } from "apps/company/api/rest/company-dashboard-resilience";
import { DashboardTilesProvider } from "apps/company/api/rest/company-dashboard-tiles";
import { useTranslation } from "lib/i18n/use-translation";

import { DASHBOARD_CONTACT } from "./contact";
import LiveBanner from "./LiveBanner";
import { DashboardTab, useDashboardTracking } from "./tracking";
import BusinessImpactTab from "./business-impact/BusinessImpactTab";
import EngagementTab from "./engagement/EngagementTab";
import EngagementRedesign from "./engagement-redesign/EngagementRedesign";
import ReportsLibraryTab from "./reports-library/ReportsLibraryTab";
import ResilienceTab from "./resilience/ResilienceTab";
import styles from "./company-dashboard.module.scss";

const ENGAGEMENT_PATH = "/client/company-dashboard/engagement";
const RESILIENCE_PATH = "/client/company-dashboard/resilience";
const BUSINESS_IMPACT_PATH = "/client/company-dashboard/business-impact";
const REPORTS_PATH = "/client/company-dashboard/reports";

const TAB_BY_PATH: Record<string, DashboardTab> = {
  [ENGAGEMENT_PATH]: "engagement",
  [RESILIENCE_PATH]: "resilience",
  [BUSINESS_IMPACT_PATH]: "business_impact",
  [REPORTS_PATH]: "reports",
};
const INTERVENTIONS_URL = "https://wone-interventions.walkingonearth.com/";

const EngagementIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 20v-6" />
    <path d="M12 20V4" />
    <path d="M18 20v-9" />
  </svg>
);

const ResilienceIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2.75 20 7.375v9.25L12 21.25 4 16.625v-9.25z" />
  </svg>
);

const BusinessImpactIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2v20" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ReportsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h6" />
  </svg>
);

const BookIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
    <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    className={styles["tab-external-arrow"]}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
);

const CompanyDashboard = () => {
  const { t } = useTranslation(["admin"]);
  const {
    resilience,
    engagement,
    reports,
    businessImpactSample,
    banner,
    company,
  } = useCompanyDashboardAccess();
  // The same answers that decide what renders below, so the banner never
  // announces a page the admin cannot see. The Engagement tab always shows,
  // so it counts once it is the redesigned page.
  const everyTabShown =
    engagement && resilience && businessImpactSample && reports;
  const { pathname } = useLocation();
  const { viewed, reportAccessed, contactClicked, reportEmailsToggled } =
    useDashboardTracking();
  const path = TAB_BY_PATH[pathname.replace(/\/+$/, "")];
  const tab =
    (path === "business_impact" && !businessImpactSample) ||
    (path === "reports" && !reports)
      ? undefined
      : path;

  useEffect(() => {
    if (tab) viewed(tab);
  }, [tab, viewed]);

  return (
    <div className={clsx(styles["wone-tokens"], styles["page"])}>
      {banner && everyTabShown && (
        <LiveBanner companyId={company?.id ?? null} />
      )}
      {banner && !everyTabShown && (
        <section className={styles["banner"]} role="note">
          <p className={styles["banner-title"]}>
            {t("admin:companyDashboard.banner.title")}
          </p>
          <p>{t("admin:companyDashboard.banner.body")}</p>
          <p>
            <Trans
              i18nKey="admin:companyDashboard.banner.contact"
              components={{
                email: (
                  <a href={`mailto:${DASHBOARD_CONTACT}`}>
                    {DASHBOARD_CONTACT}
                  </a>
                ),
              }}
            />
          </p>
        </section>
      )}
      <h1 className={styles["title"]}>{t("admin:companyDashboard.title")}</h1>
      <section className={styles["welcome"]}>
        <h2 className={styles["welcome-title"]}>
          {t("admin:companyDashboard.welcome.title")}
        </h2>
        <ul className={styles["welcome-list"]}>
          <li>
            <strong>
              {t("admin:companyDashboard.welcome.label", {
                name: t("admin:companyDashboard.engagement.tab"),
              })}
            </strong>{" "}
            {t("admin:companyDashboard.welcome.engagement")}
          </li>
          {resilience && (
            <li>
              <strong>
                {t("admin:companyDashboard.welcome.label", {
                  name: t("admin:companyDashboard.resilience.tab"),
                })}
              </strong>{" "}
              {t("admin:companyDashboard.welcome.resilience")}
            </li>
          )}
          {businessImpactSample && (
            <li>
              <strong>
                {t("admin:companyDashboard.welcome.label", {
                  name: t("admin:companyDashboard.businessImpact.tab"),
                })}
              </strong>{" "}
              {t("admin:companyDashboard.welcome.businessImpact")}
            </li>
          )}
          {reports && (
            <li>
              <strong>
                {t("admin:companyDashboard.welcome.label", {
                  name: t("admin:companyDashboard.reportsLibrary.tab"),
                })}
              </strong>{" "}
              {t("admin:companyDashboard.welcome.reports")}
            </li>
          )}
        </ul>
      </section>

      <nav className={styles["tabs"]}>
        <NavLink className={styles["tab"]} to={ENGAGEMENT_PATH}>
          <EngagementIcon />
          <span>{t("admin:companyDashboard.engagement.tab")}</span>
        </NavLink>
        {resilience && (
          <NavLink className={styles["tab"]} to={RESILIENCE_PATH}>
            <ResilienceIcon />
            <span>{t("admin:companyDashboard.resilience.tab")}</span>
          </NavLink>
        )}
        {businessImpactSample && (
          <NavLink className={styles["tab"]} to={BUSINESS_IMPACT_PATH}>
            <BusinessImpactIcon />
            <span>{t("admin:companyDashboard.businessImpact.tab")}</span>
          </NavLink>
        )}
        {reports && (
          <NavLink className={styles["tab"]} to={REPORTS_PATH}>
            <ReportsIcon />
            <span>{t("admin:companyDashboard.reportsLibrary.tab")}</span>
          </NavLink>
        )}
        <a
          className={clsx(styles["tab"], styles["tab-external"])}
          href={INTERVENTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BookIcon />
          <span>{t("admin:companyDashboard.interventions.tab")}</span>
          <span className={styles["visually-hidden"]}>
            {" "}
            {t("admin:companyDashboard.interventions.newTab")}
          </span>
          <ExternalLinkIcon />
        </a>
      </nav>

      <DashboardTilesProvider>
        <DashboardResilienceProvider>
          <DashboardEngagementProvider>
            <Routes>
              <Route
                path="engagement"
                element={
                  engagement ? <EngagementRedesign /> : <EngagementTab />
                }
              />
              <Route path="resilience" element={<ResilienceTab />} />
              {businessImpactSample && (
                <Route path="business-impact" element={<BusinessImpactTab />} />
              )}
              {reports && (
                <Route
                  path="reports"
                  element={
                    <ReportsLibraryTab
                      onViewed={(report) => reportAccessed("viewed", report)}
                      onOpened={(report) => reportAccessed("opened", report)}
                      onDownloaded={(report) =>
                        reportAccessed("downloaded", report)
                      }
                      onContactClicked={() =>
                        contactClicked("reports", "email")
                      }
                      onEmailsToggled={reportEmailsToggled}
                    />
                  }
                />
              )}
              <Route
                path="*"
                element={<Navigate to={ENGAGEMENT_PATH} replace />}
              />
            </Routes>
          </DashboardEngagementProvider>
        </DashboardResilienceProvider>
      </DashboardTilesProvider>
    </div>
  );
};

export default CompanyDashboard;
